import React, { useState, useRef } from 'react';
import { exportElementToPdf } from '../../lib/pdfExport';
import { calculateRateCardMetrics, generateRateCardMarkdown } from '../../lib/toolCalculations';

interface RateCardStudioProps {
  isUnlocked?: boolean;
  onOpenCheckout?: (tier?: 'single' | 'bundle') => void;
}

export const RateCardStudio: React.FC<RateCardStudioProps> = ({
  isUnlocked = false,
  onOpenCheckout,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [operatorName, setOperatorName] = useState('Alex Vance');
  const [studioName, setStudioName] = useState('Vance Systems Architecture');
  const [domainRole, setDomainRole] = useState('Senior Cloud & Data Engineer');
  const [vendorTaxId, setVendorTaxId] = useState('EIN: 84-2910482');
  const [legalJurisdiction, setLegalJurisdiction] = useState('State of Delaware, USA');
  const [targetNet, setTargetNet] = useState(160000);
  const [taxRate, setTaxRate] = useState(30);
  const [overheadPct, setOverheadPct] = useState(12);
  const [daysPerMonth, setDaysPerMonth] = useState(12);
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'GBP'>('USD');
  const [copied, setCopied] = useState(false);

  const isWatermarked = !isUnlocked;

  // Pure Mathematical Floor & Tier Calculation
  const {
    annualCapacityDays,
    totalGrossRequired,
    calculatedFloorDayRate,
    tier1Price,
    tier2Price,
    tier3Price,
  } = calculateRateCardMetrics({ targetNet, taxRate, overheadPct, daysPerMonth });

  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£';

  const handleCopyMarkdown = () => {
    const md = generateRateCardMarkdown({
      studioName,
      operatorName,
      domainRole,
      vendorTaxId,
      legalJurisdiction,
      currency,
      calculatedFloorDayRate,
      tier1Price,
      tier2Price,
      tier3Price,
      isWatermarked,
    });

    navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      await exportElementToPdf(documentRef.current, {
        filename: `Rate-Card-Schedule-${studioName.replace(/[^a-zA-Z0-9_-]/g, '-')}-REF-2026-RC-482.pdf`,
        documentTitle: `Rate Schedule · ${studioName}`,
        isUnlocked,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      window.print();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Top Banner (Hidden in PDF / Print) */}
      <div className="no-print screen-only flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#232736] pb-6">
        <div>
          <div className="text-[11px] font-mono text-sky-400 uppercase tracking-widest flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            TOOL 01 // DYNAMIC SPRINT MENU &amp; RATE CARD STUDIO
          </div>
          <h2 className="font-tech text-3xl font-bold text-white mt-1">
            The Interactive Rate Card Studio
          </h2>
          <p className="text-xs text-[#8b92a5] font-mono mt-1">
            Calculate your non-negotiable billable floor and compile an executive 3-tier sprint rate card.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isUnlocked ? (
            <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              PRO UNLOCKED
            </span>
          ) : (
            <button
              onClick={() => onOpenCheckout?.('single')}
              className="px-2.5 py-1.5 rounded bg-sky-400 hover:bg-sky-300 text-black font-mono text-[10px] font-bold transition flex items-center gap-1.5 shadow-[0_0_10px_rgba(56,189,248,0.2)]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              UNLOCK PRO ($9)
            </button>
          )}
          <button
            onClick={handleCopyMarkdown}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            {copied ? '✓ Copied Markdown' : 'Copy Markdown'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            className="px-3.5 py-1.5 bg-sky-400 hover:bg-sky-300 disabled:opacity-50 text-black font-tech font-bold text-xs rounded transition flex items-center gap-1.5 shadow-[0_0_12px_rgba(56,189,248,0.25)]"
          >
            {isExporting ? (
              <>
                <span className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isUnlocked ? 'Download Vector PDF' : 'Download PDF (Free)'}
              </>
            )}
          </button>
          <button
            onClick={() => window.print()}
            title="Print to physical printer"
            className="px-2.5 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-[#8b92a5] hover:text-white rounded transition flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H9v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Controls Column */}
        <div className="lg:col-span-5 space-y-5 no-print">
          <div className="blueprint-card rounded-xl p-5 border border-[#232736] space-y-4">
            <h3 className="font-tech text-base font-bold text-white border-b border-[#232736] pb-2">
              Operator &amp; Financial Parameters
            </h3>

            <div className="space-y-3 font-mono text-xs">
              <div>
                <label className="text-[11px] text-[#8b92a5] uppercase block mb-1">
                  Operator Name &amp; Studio
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={operatorName}
                    onChange={(e) => setOperatorName(e.target.value)}
                    className="px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                    placeholder="Alex Vance"
                  />
                  <input
                    type="text"
                    value={studioName}
                    onChange={(e) => setStudioName(e.target.value)}
                    className="px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                    placeholder="Vance Studio"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-[#8b92a5] uppercase block mb-1">
                  Specialist Domain / Title
                </label>
                <input
                  type="text"
                  value={domainRole}
                  onChange={(e) => setDomainRole(e.target.value)}
                  className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  placeholder="Senior Cloud Systems Architect"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Vendor Tax ID / EIN
                  </label>
                  <input
                    type="text"
                    value={vendorTaxId}
                    onChange={(e) => setVendorTaxId(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                    placeholder="EIN: 84-2910482"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Legal Jurisdiction
                  </label>
                  <input
                    type="text"
                    value={legalJurisdiction}
                    onChange={(e) => setLegalJurisdiction(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                    placeholder="State of Delaware, USA"
                  />
                </div>
              </div>

              <div className="border-t border-[#232736] pt-3 space-y-3">
                <div>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-[#8b92a5] uppercase">Target Net Annual Income:</span>
                    <span className="text-white font-bold">${targetNet.toLocaleString()}</span>
                  </div>
                  <input
                    type="range"
                    min="80000"
                    max="350000"
                    step="10000"
                    value={targetNet}
                    onChange={(e) => setTargetNet(Number(e.target.value))}
                    className="w-full accent-sky-400 cursor-pointer"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                      Tax Reserve ({taxRate}%)
                    </label>
                    <input
                      type="range"
                      min="15"
                      max="45"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      className="w-full accent-sky-400 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                      Tool/Overhead ({overheadPct}%)
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="25"
                      value={overheadPct}
                      onChange={(e) => setOverheadPct(Number(e.target.value))}
                      className="w-full accent-sky-400 cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-[#8b92a5] uppercase">Billable Days / Month:</span>
                    <span className="text-white font-bold">{daysPerMonth} days ({annualCapacityDays}/yr)</span>
                  </div>
                  <input
                    type="range"
                    min="6"
                    max="18"
                    value={daysPerMonth}
                    onChange={(e) => setDaysPerMonth(Number(e.target.value))}
                    className="w-full accent-sky-400 cursor-pointer"
                  />
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Pro tip: Solo operators cap out at 12 billable days/mo. Other days are sales & admin.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#232736] pt-3">
                <span className="text-[11px] text-[#8b92a5] uppercase">Currency:</span>
                <div className="flex gap-1.5">
                  {(['USD', 'EUR', 'GBP'] as const).map((curr) => (
                    <button
                      key={curr}
                      onClick={() => setCurrency(curr)}
                      className={`px-2 py-0.5 text-[11px] font-mono rounded ${
                        currency === curr
                          ? 'bg-sky-500 text-black font-bold'
                          : 'bg-[#10121a] text-[#8b92a5] border border-[#232736]'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#232736] pt-3">
                <span className="text-[11px] text-[#8b92a5] uppercase">Vector PDF Status:</span>
                {isUnlocked ? (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                    Pro Unbranded (Active)
                  </span>
                ) : (
                  <button
                    onClick={() => onOpenCheckout?.('single')}
                    className="text-[10px] px-2 py-0.5 rounded font-mono bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 transition font-bold"
                  >
                    Watermarked · Unlock Pro ($9)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mathematical Proof Box */}
          <div className="p-4 rounded-xl bg-[#10121a] border border-[#232736] font-mono text-xs space-y-2">
            <div className="text-[10px] text-sky-400 uppercase tracking-widest font-bold">
              OPERATIONAL BASELINE METRICS
            </div>
            <div className="flex justify-between text-zinc-300">
              <span>Required Gross Revenue:</span>
              <span className="text-white font-bold">${Math.round(totalGrossRequired).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-zinc-300">
              <span>Annual Billable Production Days:</span>
              <span className="text-white font-bold">{annualCapacityDays} days</span>
            </div>
            <div className="flex justify-between text-sky-300 border-t border-[#232736] pt-1.5 text-sm font-bold">
              <span>NON-NEGOTIABLE DAY FLOOR:</span>
              <span>{currencySymbol}{calculatedFloorDayRate.toLocaleString()}/day</span>
            </div>
          </div>
        </div>

        {/* Live Rate Card Preview (What the Client Sees) */}
        <div className="lg:col-span-7">
          <div ref={documentRef} className="print-content blueprint-card rounded-2xl p-6 sm:p-8 border border-sky-500/30 space-y-6 bg-[#0c0e14]">
            {/* Executive Document Header & Letterhead */}
            <div className="print-doc-header border-b border-[#232736] pb-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-mono text-sky-400 uppercase tracking-widest font-bold block">
                    COMMERCIAL RATE SCHEDULE &amp; CAPACITY COMMITMENT
                  </span>
                  <h3 className="font-tech text-2xl sm:text-3xl font-bold text-white mt-0.5">
                    Schedule of Consulting Fees &amp; Sprints
                  </h3>
                </div>
                <div className="text-right font-mono text-xs">
                  <span className="text-[10px] text-[#8b92a5] uppercase block">DOCUMENT REF</span>
                  <span className="text-white font-bold tracking-wider">REF-2026-RC-482</span>
                </div>
              </div>

              {/* Metadata Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                <div>
                  <span className="block text-zinc-500 uppercase">Effective Date:</span>
                  <span className="text-zinc-300 font-bold">{new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Rate Guarantee:</span>
                  <span className="text-zinc-300 font-bold">30 Calendar Days (Firm)</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Currency:</span>
                  <span className="text-zinc-300 font-bold">{currency} (ISO 4217)</span>
                </div>
                <div>
                  <span className="block text-zinc-500 uppercase">Governing Law:</span>
                  <span className="text-sky-400 font-bold">{legalJurisdiction}</span>
                </div>
              </div>

              {/* Practice Principal Profile Box */}
              <div className="p-3.5 rounded-xl bg-[#10121a] border border-[#232736] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-mono">
                <div>
                  <span className="text-[10px] text-zinc-500 uppercase block font-bold">PRACTICE PRINCIPAL &amp; ENTITY:</span>
                  <span className="text-white font-bold block">{operatorName} · {domainRole}</span>
                  <span className="text-[11px] text-[#8b92a5]">{studioName} LLC · {vendorTaxId}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-zinc-500 uppercase block font-bold">CAPACITY &amp; REMITTANCE:</span>
                  <span className="text-sky-300 font-bold block text-[11px]">Strict Dedicated Queue (1 Active Client / Sprint)</span>
                  <span className="text-[10px] text-zinc-400">Invoices Due Net-15 Strict · ACH / Wire</span>
                </div>
              </div>
            </div>

            {/* 3 Tiers (Structured Deliverables) */}
            <div className="space-y-4">
              {/* Tier 1 */}
              <div className="print-card print-avoid-break p-4 rounded-xl bg-[#10121a] border border-[#232736] space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#232736] pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-sky-400 uppercase bg-sky-500/10 px-1.5 py-0.5 rounded border border-sky-500/20 font-bold">
                      TIER 01 · 48 HOURS
                    </span>
                    <h4 className="font-tech text-base font-bold text-white">Diagnostic Architecture Audit</h4>
                  </div>
                  <div className="text-right shrink-0 font-mono">
                    <span className="text-xl font-bold text-white">{currencySymbol}{tier1Price.toLocaleString()}</span>
                    <span className="text-[10px] text-zinc-500 block">Fixed-Fee · 100% Upfront</span>
                  </div>
                </div>
                <p className="text-xs font-sans text-zinc-300 leading-relaxed">
                  Comprehensive forensic inspection of database query plans, client state hydration paths, and infrastructure bottlenecks. Deliverable includes an executive remediation memo and recorded video walkthrough.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 text-[11px] font-sans pt-1 border-t border-[#232736]/60">
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-emerald-400 uppercase font-bold block">INCLUSIONS:</span>
                    <div className="text-zinc-300">✓ Full codebase &amp; dependency audit</div>
                    <div className="text-zinc-300">✓ 30-min recorded video walkthrough</div>
                    <div className="text-zinc-300">✓ Prioritized remediation roadmap</div>
                    <div className="text-zinc-300">✓ Guaranteed 48-hour delivery commitment</div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase font-bold block">EXCLUSIONS:</span>
                    <div className="text-zinc-400">✕ Direct code refactoring or deployments</div>
                    <div className="text-zinc-400">✕ Synchronous live advisory calls</div>
                  </div>
                </div>
              </div>

              {/* Tier 2 (Highlighted) */}
              <div className="print-card print-avoid-break p-5 rounded-xl bg-[#141824] border border-sky-500/50 shadow-[0_0_20px_rgba(56,189,248,0.1)] space-y-3 relative">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#232736] pb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-black font-bold uppercase bg-sky-400 px-1.5 py-0.5 rounded">
                      TIER 02 · 2 WEEKS
                    </span>
                    <h4 className="font-tech text-base font-bold text-white">Core Implementation Sprint</h4>
                  </div>
                  <div className="text-right shrink-0 font-mono">
                    <span className="text-2xl font-bold text-sky-300">{currencySymbol}{tier2Price.toLocaleString()}</span>
                    <span className="text-[10px] text-emerald-400 font-bold block">Primary Engagement Block</span>
                  </div>
                </div>
                <p className="text-xs font-sans text-zinc-200 leading-relaxed">
                  Locked 10-day dedicated execution sprint. The Provider reserves calendar capacity exclusively for Client with zero concurrent production workload. Includes complete milestone delivery, staging verification, and PR review.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 text-[11px] font-sans pt-1 border-t border-[#232736]/60">
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-emerald-400 uppercase font-bold block">INCLUSIONS:</span>
                    <div className="text-zinc-200">✓ Full milestone feature delivery / refactor</div>
                    <div className="text-zinc-200">✓ Staging deployment &amp; integration test suite</div>
                    <div className="text-zinc-200">✓ PR reviews &amp; architecture documentation</div>
                    <div className="text-zinc-200">✓ 7-day post-launch warranty support window</div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase font-bold block">EXCLUSIONS:</span>
                    <div className="text-zinc-400">✕ Scope expansions outside Exhibit A</div>
                    <div className="text-zinc-400">✕ Unversioned third-party API refactors</div>
                  </div>
                </div>
              </div>

              {/* Tier 3 */}
              <div className="print-card print-avoid-break p-4 rounded-xl bg-[#10121a] border border-[#232736] space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#232736] pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-[#8b92a5] uppercase bg-[#181b26] px-1.5 py-0.5 rounded border border-[#232736] font-bold">
                      TIER 03 · MONTHLY
                    </span>
                    <h4 className="font-tech text-base font-bold text-white">Retained Advisory &amp; Production Queue</h4>
                  </div>
                  <div className="text-right shrink-0 font-mono">
                    <span className="text-xl font-bold text-white">{currencySymbol}{tier3Price.toLocaleString()}<span className="text-xs text-zinc-500 font-normal">/mo</span></span>
                    <span className="text-[10px] text-zinc-500 block">Monthly Recurring Retainer</span>
                  </div>
                </div>
                <p className="text-xs font-sans text-zinc-300 leading-relaxed">
                  Continuous strategic architecture guidance and prioritized asynchronous ticket processing for technical founders and engineering leadership teams.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 text-[11px] font-sans pt-1 border-t border-[#232736]/60">
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-emerald-400 uppercase font-bold block">INCLUSIONS:</span>
                    <div className="text-zinc-300">✓ 2 reserved sprint execution days / month</div>
                    <div className="text-zinc-300">✓ Priority asynchronous SLA (&lt; 24h turnaround)</div>
                    <div className="text-zinc-300">✓ Bi-weekly executive architecture consultations</div>
                    <div className="text-zinc-300">✓ Continuous pull request &amp; design reviews</div>
                  </div>
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase font-bold block">EXCLUSIONS:</span>
                    <div className="text-zinc-400">✕ Rollover of unused monthly sprint capacity</div>
                    <div className="text-zinc-400">✕ Weekend / after-hours on-call paging</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Commercial Terms & Policies */}
            <div className="print-card print-avoid-break p-4 rounded-xl bg-[#10121a] border border-[#232736] font-sans text-[11px] space-y-1.5 text-zinc-300">
              <div className="text-white font-bold uppercase tracking-wider text-[11px] mb-1 font-mono">
                COMMERCIAL ENGAGEMENT POLICIES &amp; SETTLEMENT PROTOCOL
              </div>
              <p>• <strong>Payment Schedule:</strong> 50% reservation deposit upon execution to lock calendar slot; 50% upon milestone staging cutover. Invoices payable Net-15.</p>
              <p>• <strong>Automatic Capacity Freeze:</strong> Invoices past due by &gt; 5 days trigger an immediate, non-penalized production freeze until settled in full.</p>
              <p>• <strong>Capacity Exclusivity:</strong> Contractor maintains strict single-client focus during active sprint blocks. Zero concurrent client workload.</p>
              <p>• <strong>Rate Validity:</strong> Published rates guaranteed for 30 calendar days from issuance. Calendar reservations confirmed solely upon receipt of deposit.</p>
            </div>

            {/* Formal Execution & Signature Block */}
            <div className="print-signature-block print-avoid-break grid grid-cols-2 gap-6 pt-5 border-t border-[#232736] font-mono">
              <div className="space-y-2">
                <span className="text-[10px] text-zinc-500 uppercase block font-bold">CLIENT ENTITY ACCEPTANCE:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  Client Representative
                </div>
                <span className="text-[10px] text-zinc-600 block">AUTHORIZED CORPORATE SIGNATORY &amp; DATE</span>
              </div>
              <div className="space-y-2">
                <span className="text-[10px] text-zinc-500 uppercase block font-bold">SERVICE PROVIDER ACCEPTANCE:</span>
                <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                  {operatorName}
                </div>
                <span className="text-[10px] text-zinc-600 block">MANAGING MEMBER · {studioName} LLC ({vendorTaxId})</span>
              </div>
            </div>

            {/* Terms Footer / Watermark */}
            {isWatermarked && (
              <div className="print-doc-footer border-t border-[#232736] pt-3 text-right text-[10px] font-mono text-sky-400/80">
                Generated with Bureau // Rate Studio · Pro Unlocks Unbranded Vector PDF Export
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
